import { useState, useCallback, useEffect } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { Heart, X, Star, RotateCcw, Sparkles, Undo2 } from "lucide-react";

/**
 * SwipeDeck - Tinder-style stacked card deck with drag-to-swipe.
 *
 * Props:
 *  - cards: Array<{ id, render: (helpers) => JSX, accent?: string }>
 *  - onSwipe?: (dir: 1|-1, card) => void
 *  - emptyState?: JSX (rendered when deck exhausted)
 */
export function SwipeDeck({ cards, onSwipe, emptyState, onReset }) {
  const [index, setIndex] = useState(0);
  const [history, setHistory] = useState([]); // last swiped directions

  const handleSwipe = useCallback(
    (dir) => {
      const card = cards[index];
      setHistory((h) => [...h, { index, dir }]);
      setIndex((i) => Math.min(i + 1, cards.length));
      onSwipe?.(dir, card);
    },
    [cards, index, onSwipe]
  );

  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setIndex(last.index);
  }, [history]);

  const reset = useCallback(() => {
    setIndex(0);
    setHistory([]);
    onReset?.();
  }, [onReset]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (index >= cards.length) return;
      if (e.key === "ArrowLeft") handleSwipe(-1);
      else if (e.key === "ArrowRight") handleSwipe(1);
      else if (e.key === "ArrowUp") handleSwipe(2);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, cards.length, handleSwipe]);

  const exhausted = index >= cards.length;
  const stack = cards.slice(index, index + 3); // current + 2 behind

  return (
    <div className="relative w-full select-none" data-testid="swipe-deck">
      {/* Progress dots */}
      <div className="mb-4 flex flex-wrap items-center justify-center gap-1.5 px-4">
        {cards.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index
                ? "w-7 bg-gradient-to-r from-[#ff4458] to-[#fd267a]"
                : i < index
                ? "w-4 bg-white/40"
                : "w-4 bg-white/12"
            }`}
          />
        ))}
        <span className="ml-3 font-mono text-[11px] text-[#a8adbd]">
          {Math.min(index + 1, cards.length)}/{cards.length}
        </span>
      </div>

      {/* Card area */}
      <div
        className="relative mx-auto"
        style={{ height: 600, maxWidth: 420 }}
      >
        {/* Decorative stacked silhouettes behind */}
        {!exhausted && (
          <>
            <div className="pointer-events-none absolute inset-x-6 top-3 h-[580px] -rotate-3 rounded-[32px] border border-[#222636]/60 bg-[#161922]/50" />
            <div className="pointer-events-none absolute inset-x-3 top-1 h-[590px] rotate-2 rounded-[32px] border border-[#222636]/70 bg-[#181b24]/70" />
          </>
        )}

        <AnimatePresence initial={false}>
          {!exhausted &&
            stack
              .slice()
              .reverse()
              .map((card, revIdx) => {
                const offset = stack.length - 1 - revIdx; // 0 = top
                const isTop = offset === 0;
                return (
                  <SwipeCard
                    key={card.id}
                    card={card}
                    isTop={isTop}
                    offset={offset}
                    onSwipe={handleSwipe}
                  />
                );
              })}
        </AnimatePresence>

        {exhausted && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute inset-0 flex flex-col items-center justify-center rounded-[32px] border border-[#222636] bg-gradient-to-br from-[#181b24] via-[#1a1d28] to-[#0f1117] p-8 text-center shadow-[0_30px_80px_rgba(253,38,122,0.15)]"
            data-testid="deck-empty-state"
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#ff4458] to-[#fd267a] shadow-[0_12px_30px_rgba(253,38,122,0.45)]">
              <Sparkles className="h-9 w-9 text-white" />
            </div>
            {emptyState || (
              <>
                <h3 className="mt-5 font-display text-2xl font-bold text-white">
                  To wszystko!
                </h3>
                <p className="mt-2 max-w-xs text-sm text-[#a8adbd]">
                  Przejrzałeś wszystkie karty analizy. Możesz wrócić do początku.
                </p>
              </>
            )}
            <button
              type="button"
              onClick={reset}
              data-testid="deck-reset"
              className="mt-6 inline-flex items-center gap-2 rounded-full dc-gradient-bg px-6 py-3 font-display text-sm font-semibold text-white shadow-[0_10px_28px_rgba(253,38,122,0.35)] transition-transform hover:-translate-y-0.5"
            >
              <RotateCcw className="h-4 w-4" /> Pokaż jeszcze raz
            </button>
          </motion.div>
        )}
      </div>

      {/* Action buttons */}
      <div className="mt-6 flex items-center justify-center gap-4 sm:gap-5">
        <ActionButton
          color="#fd267a"
          onClick={() => !exhausted && handleSwipe(-1)}
          disabled={exhausted}
          testId="deck-nope"
          size="md"
          ariaLabel="Nope"
        >
          <X className="h-6 w-6" strokeWidth={3} />
        </ActionButton>

        <ActionButton
          color="#3b82f6"
          onClick={handleUndo}
          disabled={history.length === 0}
          testId="deck-undo"
          size="sm"
          ariaLabel="Cofnij"
        >
          <Undo2 className="h-4 w-4" strokeWidth={3} />
        </ActionButton>

        <ActionButton
          color="#fdb022"
          onClick={() => !exhausted && handleSwipe(2)}
          disabled={exhausted}
          testId="deck-super"
          size="sm"
          ariaLabel="Super Like"
        >
          <Star className="h-5 w-5" strokeWidth={2.5} fill="#fdb022" />
        </ActionButton>

        <ActionButton
          color="#32d583"
          onClick={() => !exhausted && handleSwipe(1)}
          disabled={exhausted}
          testId="deck-like"
          size="md"
          ariaLabel="Like"
        >
          <Heart className="h-6 w-6" strokeWidth={2.5} fill="#32d583" />
        </ActionButton>
      </div>

      <p className="mt-4 text-center text-[11px] text-[#6a6f80]">
        Przeciągnij kartę w lewo/prawo lub użyj strzałek
      </p>
    </div>
  );
}

function ActionButton({ color, onClick, disabled, children, testId, ariaLabel, size = "md" }) {
  const dims = size === "md" ? "h-14 w-14" : "h-11 w-11";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={ariaLabel}
      className={`${dims} flex items-center justify-center rounded-full border-2 bg-[#0f1117] shadow-lg transition-transform duration-200 hover:-translate-y-1 hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:scale-100`}
      style={{
        borderColor: color,
        color,
        boxShadow: `0 10px 24px ${color}33, inset 0 0 0 1px ${color}1f`,
      }}
    >
      {children}
    </button>
  );
}

/* -------------------- Single swipeable card -------------------- */

function SwipeCard({ card, isTop, offset, onSwipe }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 0, 300], [-18, 0, 18]);

  const likeOpacity = useTransform(x, [40, 160], [0, 1]);
  const nopeOpacity = useTransform(x, [-160, -40], [1, 0]);
  const superOpacity = useTransform(y, [-160, -40], [1, 0]);

  // background shadow / vignette intensity follows drag
  const shadowOpacity = useTransform(
    [x, y],
    ([latestX, latestY]) =>
      Math.min(1, (Math.abs(latestX) + Math.abs(latestY)) / 220)
  );

  const onDragEnd = (_e, info) => {
    const { offset: o, velocity: v } = info;
    const swipeX = Math.abs(o.x) > 110 || Math.abs(v.x) > 600;
    const swipeUp = o.y < -110 || v.y < -600;

    if (swipeUp) {
      onSwipe(2);
    } else if (swipeX) {
      onSwipe(o.x > 0 ? 1 : -1);
    }
  };

  // Base scale & y for stack position
  const baseScale = 1 - offset * 0.05;
  const baseY = offset * 10;

  return (
    <motion.div
      className="absolute inset-0"
      data-testid={isTop ? "swipe-card-top" : `swipe-card-stack-${offset}`}
      drag={isTop ? true : false}
      dragElastic={0.7}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragMomentum={false}
      onDragEnd={isTop ? onDragEnd : undefined}
      style={isTop ? { x, y, rotate, zIndex: 10 - offset } : { zIndex: 10 - offset }}
      initial={{
        scale: baseScale - 0.05,
        y: baseY + 20,
        opacity: 0,
      }}
      animate={{
        scale: baseScale,
        y: baseY,
        opacity: 1,
      }}
      exit={{
        x: x.get() > 0 ? 800 : x.get() < 0 ? -800 : 0,
        y: y.get() < -50 ? -1200 : y.get(),
        rotate: x.get() > 0 ? 30 : x.get() < 0 ? -30 : 0,
        opacity: 0,
        transition: { duration: 0.45, ease: [0.32, 0.72, 0, 1] },
      }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
      whileTap={isTop ? { cursor: "grabbing" } : {}}
    >
      <div
        className="relative h-full w-full overflow-hidden rounded-[32px] border border-[#222636] bg-[#181b24] shadow-[0_30px_70px_rgba(0,0,0,0.5)]"
        style={{ cursor: isTop ? "grab" : "default" }}
      >
        {/* Content rendered by parent */}
        {card.render({ isTop })}

        {/* Drag indicators - only on top card */}
        {isTop && (
          <>
            <motion.div
              style={{ opacity: likeOpacity }}
              className="pointer-events-none absolute left-6 top-8 -rotate-12 rounded-2xl border-4 border-[#32d583] px-4 py-1.5"
            >
              <span className="font-display text-2xl font-extrabold tracking-wider text-[#32d583]">
                LIKE
              </span>
            </motion.div>
            <motion.div
              style={{ opacity: nopeOpacity }}
              className="pointer-events-none absolute right-6 top-8 rotate-12 rounded-2xl border-4 border-[#fd267a] px-4 py-1.5"
            >
              <span className="font-display text-2xl font-extrabold tracking-wider text-[#fd267a]">
                NOPE
              </span>
            </motion.div>
            <motion.div
              style={{ opacity: superOpacity }}
              className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 rounded-2xl border-4 border-[#fdb022] px-4 py-1.5"
            >
              <span className="font-display text-2xl font-extrabold tracking-wider text-[#fdb022]">
                SUPER
              </span>
            </motion.div>
          </>
        )}

        {/* Subtle vignette overlay following drag */}
        {isTop && (
          <motion.div
            style={{ opacity: shadowOpacity }}
            className="pointer-events-none absolute inset-0 bg-black/30"
          />
        )}
      </div>
    </motion.div>
  );
}
